import { Link } from "react-router-dom";
import { SectionHeading } from "@/components/site/SectionHeading";
import { MarqueeTrack, useMarquee } from "@/components/site/ScrollTrack";
import { SafeImage } from "@/components/site/SafeImage";
import { HOME_DEVELOPERS, type DeveloperContent } from "@/lib/developers";
import { DEVELOPER_LOGOS } from "@/lib/developer-logos";

/**
 * Homepage "Top Dubai Developers" — developer cards (cover photo +
 * white logo plate) on a full-bleed carousel that drifts leftwards on
 * its own and pauses under the pointer or finger. Each card links to the
 * developer's dedicated page at /developers/<slug>.
 */
export function DeveloperStrip() {
  const { trackRef } = useMarquee({ speed: 42 });

  return (
    <section
      aria-label="Top Dubai developers"
      className="overflow-hidden border-y border-[var(--line)] bg-[var(--bg-2)]"
    >
      <div className="container-wide pt-12 sm:pt-16 md:pt-20 short:pt-10">
        <SectionHeading
          eyebrow="Trusted by Dubai's Leading Developers"
          title="Top Dubai Developers."
          body="The names behind Dubai's most sought-after addresses, and every property they have on the market — one click away."
        />
      </div>

      <MarqueeTrack
        trackRef={trackRef}
        className="mt-8 gap-4 sm:mt-10 sm:gap-5"
        items={HOME_DEVELOPERS}
        keyOf={(developer) => developer.slug}
        // Phones: a share of the viewport; from `sm` the fixed 320px card
        // of the desktop design.
        itemClassName="flex w-[80%] max-w-[300px] sm:w-[320px] sm:max-w-none"
        renderItem={(developer, repeat) => (
          <DeveloperCard developer={developer} tabIndex={repeat ? -1 : undefined} />
        )}
      />

      <div className="container-wide pb-12 sm:pb-16 md:pb-20 short:pb-10">
        <div className="mt-8 flex justify-center sm:mt-10">
          <Link to="/developers" className="btn btn-primary btn-block-sm">
            Explore All Developers
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}

function DeveloperCard({
  developer,
  tabIndex,
}: {
  developer: DeveloperContent;
  tabIndex?: number;
}) {
  const logo = DEVELOPER_LOGOS[developer.slug];
  return (
    <Link
      to={`/developers/${developer.slug}`}
      tabIndex={tabIndex}
      data-on-dark
      className="group relative isolate flex h-[200px] w-full overflow-hidden border border-[rgba(211,197,150,0.18)] bg-[var(--navy)] shadow-[0_20px_60px_-46px_rgba(13,20,34,0.45)] sm:h-[220px]"
    >
      <SafeImage
        src={developer.cover}
        alt={developer.coverAlt}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-[0.8] transition-transform duration-[900ms] group-hover:scale-[1.04]"
      />
      <span
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(13,20,34,0.3)_0%,rgba(13,20,34,0.18)_38%,rgba(13,20,34,0.88)_100%)]"
      />

      {logo && (
        <span className="absolute left-3 top-3 inline-flex items-center bg-white px-3 py-2">
          <img
            src={logo.src}
            alt=""
            aria-hidden
            loading="lazy"
            className={`w-auto max-w-[120px] object-contain ${logo.className}`}
          />
        </span>
      )}

      <span className="relative flex w-full items-end justify-between gap-3 p-4">
        <span className="min-w-0">
          <span
            translate="no"
            className="block break-words font-display text-[22px] leading-[1.05] text-[var(--text-on-dark)] transition-colors duration-200 group-hover:text-[var(--gold-bright)] sm:text-[24px]"
            style={{ fontVariationSettings: '"opsz" 72' }}
          >
            {developer.name}
          </span>
          <span className="mt-1 block text-[11px] uppercase tracking-[0.16em] text-[var(--cream)]/85">
            View Properties
          </span>
        </span>
        <span
          aria-hidden
          className="inline-flex size-9 flex-none items-center justify-center border border-[rgba(240,234,216,0.45)] text-[var(--cream)] transition-all duration-200 group-hover:border-[var(--gold-bright)] group-hover:text-[var(--gold-bright)]"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.2" />
          </svg>
        </span>
      </span>
    </Link>
  );
}
